"use client"

import { useState, useEffect } from "react"
import { BellAlertSolid, CheckCircleSolid } from "@medusajs/icons"
import { subscribeToRestockNotification } from "@/lib/data/restock"
import Button from "@/modules/common/components/button"

const RESTOCK_STORAGE_KEY = "restock_subscribed_variants"

function getSubscribedVariants(): Set<string> {
  if (typeof window === "undefined") return new Set()
  try {
    const raw = sessionStorage.getItem(RESTOCK_STORAGE_KEY)
    if (!raw) return new Set()
    const ids = JSON.parse(raw) as string[]
    return new Set(Array.isArray(ids) ? ids : [])
  } catch {
    return new Set()
  }
}

function markVariantSubscribed(variantId: string) {
  if (typeof window === "undefined") return
  const set = getSubscribedVariants()
  set.add(variantId)
  sessionStorage.setItem(RESTOCK_STORAGE_KEY, JSON.stringify([...set]))
}

// Default sales channel ID - should be set in environment variables
const DEFAULT_SALES_CHANNEL_ID = process.env.NEXT_PUBLIC_DEFAULT_SALES_CHANNEL_ID || "sc_01KB0A4V4XJVZTNX98X6HSPT0B"

type RestockNotificationButtonProps = {
  variantId: string
  salesChannelId?: string
  customerEmail?: string
  customerId?: string | null
}

/**
 * Button to subscribe to restock notifications
 * Shows when a variant is out of stock and user is authenticated
 */
export default function RestockNotificationButton({
  variantId,
  salesChannelId,
  customerEmail,
  customerId,
}: RestockNotificationButtonProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Restore persisted "already subscribed" state (client-only, avoids hydration mismatch)
  useEffect(() => {
    setIsSubscribed(getSubscribedVariants().has(variantId))
  }, [variantId])

  const handleSubscribe = async () => {
    if (!customerEmail) {
      setError("E-poštni naslov ni na voljo")
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await subscribeToRestockNotification({
        variant_id: variantId,
        sales_channel_id: salesChannelId || DEFAULT_SALES_CHANNEL_ID,
        email: customerEmail,
        customer_id: customerId,
      })

      if (result.success) {
        markVariantSubscribed(variantId)
        setIsSubscribed(true)
      } else {
        setError(result.message)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Prišlo je do napake")
    } finally {
      setIsLoading(false)
    }
  }

  if (isSubscribed) {
    return (
      <div className="flex items-center gap-x-2 p-4 bg-emerald-50 rounded-lg border border-emerald-200 text-emerald-800">
        <CheckCircleSolid className="h-5 w-5 text-emerald-600 flex-shrink-0" />
        <span className="text-sm font-medium">
          Prijavljeni ste na obvestilo o ponovni razpoložljivosti!
        </span>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <Button
        onClick={handleSubscribe}
        variant="secondary"
        className="w-full h-12 md:h-10 text-base md:text-sm !bg-blue-600 !text-white hover:!bg-blue-700 !border-blue-600"
        isLoading={isLoading}
        disabled={isLoading}
      >
        <BellAlertSolid className="mr-2 w-4 h-4" />
        Obvesti me, ko bo na zalogi
      </Button>
      {error && (
        <p className="text-red-600 text-sm text-center">{error}</p>
      )}
    </div>
  )
}

